// @ts-strict-ignore
import type {Moment} from 'moment';
import moment from 'moment';
import {immerable} from 'immer';

import {formatMetricNumber} from 'common/util';
import type {AxisUnitPrecision} from 'common/report/insights/util/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';

import type {EventDefinition} from './event-definition';

interface User {
  name: string;
  email: string;
  id: number;
}

export const PROPERTY_RESOURCE_TYPE = {
  EVENT: `Event`,
  USER: `User`,
};

export type PropertyDisplayOptions = {
  abbrev?: boolean;
  axis?: BookmarkTypes.AxisAssignment;
  precision?: AxisUnitPrecision | null;
  prefix?: string;
  suffix?: string;
};

export class PropertyDefinition {
  [immerable] = true;
  public static createPropertyDefinition(definitionProps: any): PropertyDefinition {
    return new PropertyDefinition(
      definitionProps.id,
      definitionProps.hidden,
      !!definitionProps.hidden_by_data_audit,
      definitionProps.name,
      definitionProps.count ? definitionProps.count : 0,
      definitionProps.uiQueryCount || 0,
      definitionProps.apiQueryCount || 0,
      definitionProps.dropped,
      definitionProps.merged,
      definitionProps.sensitive,
      definitionProps.displayName ? definitionProps.displayName : ``,
      definitionProps.displayOptions ?? {},
      definitionProps.description ? definitionProps.description : ``,
      definitionProps.mergedPropertyId,
      definitionProps.mergeProperties,
      definitionProps.type ? definitionProps.type : ``,
      definitionProps.joinPropertyType || definitionProps.join_property_type || ``,
      definitionProps.exampleValue ? definitionProps.exampleValue : ``,
      definitionProps.selected ? definitionProps.selected : false,
      definitionProps.isMixpanelDefinition,
      // isSensitiveBlacklisted comes from the api and proly needs a future backend refactor
      // eslint-disable-next-line local-rules/naming-conventions
      definitionProps.isSensitiveBlacklisted,
      definitionProps.resourceType,
      definitionProps.required,
      definitionProps.status,
      definitionProps.createdBy,
      definitionProps.customPropertyId,
      definitionProps.dimension_data_group_id,
      definitionProps.dataGroupId,
      definitionProps.events,
      moment(definitionProps.createdUTC),
      moment(definitionProps.modifiedUTC),

      // permissions
      definitionProps.canView || definitionProps.can_view,
      definitionProps.canUpdateBasic || definitionProps.can_update_basic,
      definitionProps.canShare || definitionProps.can_share,
      definitionProps.isSuperadmin || definitionProps.is_superadmin,
      definitionProps.allowStaffOverride || definitionProps.allow_staff_override,
      definitionProps.scdEvent,
    );
  }
  constructor(
    public id: number,
    public hidden: boolean,
    public hiddenByDataAudit: boolean,
    public name: string,
    public count: number,
    public uiQueryCount: number,
    public apiQueryCount: number,
    public dropped: boolean = false,
    public merged: boolean = false,
    public sensitive: boolean = false,
    public displayName?: string,
    public displayOptions?: PropertyDisplayOptions,
    public description?: string,
    public mergedPropertyId?: number,
    public mergeProperties?: string[],
    public type?: string,
    public joinPropertyType?: string,
    public exampleValue?: string,
    public selected?: boolean,
    public isMixpanelDefinition: boolean = false,
    public isSensitiveIgnored: boolean = false,
    public resourceType?: string,
    public required: boolean = false,
    public status?: string,
    public createdBy?: User,
    public customPropertyId?: number,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public dimension_data_group_id?: string,
    public dataGroupId?: string | null,
    public events?: EventDefinition[],
    public createdUTC?: Moment,
    public modifiedUTC?: Moment,

    // permissions
    public canView?: boolean,
    public canUpdateBasic?: boolean,
    public canShare?: boolean,
    public isSuperadmin?: boolean,
    public allowStaffOverride?: boolean,
    public scdEvent?: string,
  ) {
    this.isPropertyPendingApproval =
      createdUTC?.isValid() && createdUTC.isAfter(moment().subtract(30, `days`)) && !customPropertyId;
  }

  public declare mappedDataGroupId?: string;

  public declare isPropertyPendingApproval: boolean;

  get json(): Obj {
    const {
      apiQueryCount,
      count,
      createdBy,
      customPropertyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dimension_data_group_id,
      description,
      displayName,
      displayOptions,
      dropped,
      events,
      exampleValue,
      hidden,
      id,
      isMixpanelDefinition,
      isSensitiveIgnored,
      merged,
      mergedPropertyId,
      mergeProperties,
      name,
      resourceType,
      required,
      scdEvent,
      selected,
      sensitive,
      status,
      type,
      joinPropertyType,
      uiQueryCount,
      createdUTC,
      modifiedUTC,
    } = this;

    return {
      apiQueryCount,
      count,
      createdBy,
      customPropertyId,
      dimension_data_group_id,
      description,
      displayName,
      displayOptions,
      dropped,
      events,
      exampleValue,
      hidden,
      id,
      isMixpanelDefinition,
      // isSensitiveBlacklisted comes from the api and proly needs a future backend refactor
      // eslint-disable-next-line local-rules/naming-conventions
      isSensitiveBlacklisted: isSensitiveIgnored,
      merged,
      mergedPropertyId,
      mergeProperties,
      name,
      resourceType,
      required,
      scdEvent,
      selected,
      sensitive,
      status,
      type,
      joinPropertyType,
      uiQueryCount,
      createdUTC,
      modifiedUTC,
    };
  }

  get isNotPartOfMergedProperty(): boolean {
    return !this.mergedPropertyId && (this.mergeProperties === undefined || this.mergeProperties.length > 0);
  }

  get isMerged(): boolean {
    return this.merged;
  }

  get isHidden(): boolean {
    return this.hidden;
  }

  get isVisible(): boolean {
    return !this.hidden;
  }

  get isDropped(): boolean {
    return this.dropped;
  }

  get queryCount(): number {
    return +this.uiQueryCount + +this.apiQueryCount;
  }

  get transformationStatus(): string {
    const visibility = this.hidden ? `Hidden` : `Visible`;
    const sensitive = this.sensitive ? `, Classified` : ``;
    const merged = this.merged ? `, Merged` : ``;
    const dropped = this.dropped ? `, Dropped` : ``;

    return visibility + sensitive + merged + dropped;
  }

  getTransformationStatusList(): Translatable[] {
    const statusList: Translatable[] = [
      this.hidden ? ({t}) => t(`lexicon.events.status.hidden`) : ({t}) => t(`lexicon.events.status.visible`),
    ];

    if (this.sensitive) {
      statusList.push(({t}) => t(`lexicon.events.status.classified`));
    }

    if (this.merged) {
      statusList.push(({t}) => t(`lexicon.events.status.merged`));
    }

    if (this.dropped) {
      statusList.push(({t}) => t(`lexicon.events.status.dropped`));
    }

    return statusList;
  }

  get abbreviatedCount(): string {
    if (this.customPropertyId) {
      return `N/A`;
    }
    return formatMetricNumber(this.count);
  }

  get abbreviatedQueryCount(): string {
    return formatMetricNumber(this.queryCount);
  }

  getKey() {
    // TODO: Use this for keying definition maps for events also. Would also removes the
    // need for `getKeyedValues` that adds `key` field here:
    // https://github.com/mixpanel/analytics/commit/9267bd5a8b81a01c1d34296a9dbc25a83674914e
    return this.customPropertyId ? `$custom_property:${this.customPropertyId}` : this.name;
  }
}

/** Allows interchangeable use of the Lexicon PropertyDefinition or the one defined at 'common/util/definitions-types'; useful for passing updates
 * NOTE: This works around the fact that we have 2 different types for the same data, with fields of the same name having different types.
 */
export type PropertyDefinitionWithoutType = Omit<Partial<PropertyDefinition>, `resourceType` | `type`>;
